import { ContentWrapper, Group } from '@/components/_common';
import {
  complementActionColor,
  complementActionLabels,
  complementActionOriginColor,
  complementActionOriginLabels,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.constats';
import { useWorkOrderComplementListStatusAction } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useDisclosure } from '@/hooks';
import {
  Button,
  Drawer,
  Empty,
  Skeleton,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import { PropsWithChildren, useMemo } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useIntl } from 'react-intl';
import { workorderComplementColor } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import EntityAvatar from '@/components/_common/EntityAvatar';
import StatusTag from '@/components/_common/StatusTag';
import moment from 'moment';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import TimelineItem from 'antd/lib/timeline/TimelineItem';

export default function ComplementStatusHistoryDrawer(
  props: PropsWithChildren<unknown>,
) {
  const intl = useIntl();
  const [opened, actions] = useDisclosure(false);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const listStatusAction = useWorkOrderComplementListStatusAction(
    {
      workOrderId: workorder?.id,
      complementId: complement?.id,
    },
    opened,
  );

  const histories = useMemo(() => {
    return listStatusAction.data?.pages.flatMap((page) => page.data) || [];
  }, [listStatusAction.data]);

  const getStatusLabel = (status?: string) => {
    if (!status) return '';

    const key = status.split('.').pop();

    return intl.formatMessage({
      id: `workOrders.domain.enums.work_order_status.${key}`,
    });
  };

  const getOriginData = (origin?: string) => {
    if (!origin) {
      return { label: 'Desconhecido', color: 'default' };
    }

    return {
      label: complementActionOriginLabels[origin],
      color: complementActionOriginColor[origin] || 'default',
    };
  };

  if (!hasPermissions([Authorities.MOVIMENTATION_WORKORDER_VIEW_HISTORY])) {
    return <>{props.children}</>;
  }

  return (
    <>
      <Tooltip title="Visualizar histórico">
        <span
          style={{ display: 'flex', cursor: 'pointer' }}
          onClick={actions.open}
        >
          {props.children}
        </span>
      </Tooltip>

      <Drawer
        title="Histórico de situações da O.S."
        width={600}
        open={opened}
        onClose={actions.close}
      >
        <Skeleton
          avatar
          paragraph={{ rows: 1 }}
          loading={listStatusAction.isLoading}
        >
          <ContentWrapper style={{ paddingBottom: 10 }}>
            {!histories.length && (
              <Empty description="Nenhum histórico encontrado" />
            )}

            {!!histories.length && (
              <Timeline>
                {histories.map((history) => {
                  const actionLabel = complementActionLabels[history.action];
                  const actionColor =
                    complementActionColor[history.action] || 'default';

                  const { label: originLabel, color: originColor } =
                    getOriginData(history.origin);

                  const statusColor = history.fromStatus
                    ? workorderComplementColor[history.fromStatus]
                    : 'default';

                  const statusLabel = getStatusLabel(history.fromStatus);

                  return (
                    <TimelineItem
                      key={history.id}
                      className="timeline"
                      dot={
                        <EntityAvatar
                          name={history.userName}
                          src={history.userImageUrl}
                        />
                      }
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <Group gap={6}>
                          <Typography.Text strong>
                            {history.userName || 'Não identificado'}
                          </Typography.Text>

                          <Typography.Text type="secondary">
                            alterou o status
                          </Typography.Text>

                          <StatusTag
                            size="sm"
                            color={originColor}
                            label={originLabel}
                          />
                        </Group>

                        <Group gap={6}>
                          {history.fromStatus ? (
                            <>
                              <StatusTag
                                color={statusColor}
                                label={statusLabel}
                              />
                              <Typography.Text type="secondary">
                                →
                              </Typography.Text>
                              <StatusTag
                                color={actionColor}
                                label={actionLabel}
                              />
                            </>
                          ) : (
                            <StatusTag
                              color={actionColor}
                              label={actionLabel}
                            />
                          )}
                        </Group>

                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {moment(history.actionAt).fromNow()} •{' '}
                          {moment(history.actionAt).format('DD/MM/YYYY HH:mm')}
                        </Typography.Text>
                      </div>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            )}

            {listStatusAction.hasNextPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16,
                }}
              >
                <Button
                  loading={listStatusAction.isFetchingNextPage}
                  onClick={() => listStatusAction.fetchNextPage()}
                >
                  Carregar mais
                </Button>
              </div>
            )}
          </ContentWrapper>
        </Skeleton>
      </Drawer>
    </>
  );
}
